// A folha do relatório de exames de imagem — só desenho.
//
// Nasceu em 19/09, quando chegaram oito PDFs de tomografia de uma vez. Cada
// laudo, sozinho, descreve um instante; a pergunta da família é "melhorou ou
// piorou?", e só a sequência responde. Esta folha mostra a sequência: uma
// tabela de achado por data, a leitura de cada mudança com o que a literatura
// diz sobre ela, e os laudos inteiros embaixo, porque o resumo nunca substitui
// o texto assinado.
//
// Sem busca e sem estado: `Relatorio.tsx` traz os dados, e a ferramenta
// `conferir-relatorio-imagem.mts` monta esta folha com dados reais para olhar
// no navegador antes de publicar.
import { formatarData, formatarDataHora, formatarDiaMes } from '@medicov1/shared';
import { identificar, type PacienteNoPapel } from './identificacao';

export interface DocumentoDeImagemDTO {
  gerado: { em: string; deExames: number; primeiro: string | null; ultimo: string | null };
  /** Vai para o médico: identifica como o hospital (`identificar`). */
  paciente: PacienteNoPapel;
  regioes: {
    regiao: 'torax' | 'cranio' | 'coracao';
    rotulo: string;
    exames: { id: string; nome: string; em: string; laudo: string }[];
    linhas: { chave: string; rotulo: string; marcas: ('sim' | 'nao' | 'nada')[] }[];
    mudancas: { chave: string; rotulo: string; de: 'sim' | 'nao'; para: 'sim' | 'nao'; deEm: string; paraEm: string }[];
    constantes: string[];
    leitura: { titulo: string; texto: string; fontes: { titulo: string; url: string }[] }[];
  }[];
  fontes: { titulo: string; url: string }[];
  glossario: { chave: string; rotulo: string; regiao: string; oQueE: string; porQueImporta: string | null }[];
}

/** O que cada marca da tabela quer dizer, por extenso — é o que vai na legenda. */
export const LEGENDA_DAS_MARCAS = {
  sim: 'o laudo descreve o achado',
  nao: 'o laudo afirma que não há',
  nada: 'o laudo não fala do assunto',
} as const;

const dia = formatarData;

export function FolhaImagem({ dados }: { dados: DocumentoDeImagemDTO }) {
  const quem = identificar(dados.paciente, true);
  const g = dados.gerado;

  return (
    <article className="doc doc--imagem">
      <header className="doc__cabeca">
        <div>
          <h1>{quem.nome}</h1>
          <p className="doc__quem">{quem.linha}</p>
        </div>
        <div className="doc__selo">
          <span>Exames de imagem</span>
          <small>{g.deExames} laudos{g.primeiro && g.ultimo && <>, de {formatarDiaMes(g.primeiro)} a {formatarDiaMes(g.ultimo)}</>}</small>
          <small>gerado em {formatarDataHora(g.em)}</small>
        </div>
      </header>

      <p className="doc__aviso">
        Este documento reúne os laudos de imagem em ordem de data e explica o que
        cada achado é, com base na literatura citada no fim. <b>Não é diagnóstico</b>:
        o que os achados significam juntos, e o que fazer com eles, é da equipe.
      </p>

      {/* ------------------------------------------------------------ regiões */}
      {dados.regioes.map((r) => (
        <section className="doc__bloco" key={r.regiao}>
          <h2>{r.rotulo} <small>{r.exames.length} {r.exames.length === 1 ? 'exame' : 'exames'}</small></h2>

          {/* A tabela é a peça central: uma linha por achado, uma coluna por
              exame. É onde se vê, numa olhada, o que sumiu e o que nunca mudou. */}
          {r.linhas.length > 0 && (
            <div className="doc__rolagem">
              <table className="doc__tabela doc__linhaDoTempo">
                <thead>
                  <tr>
                    <th>Achado</th>
                    {r.exames.map((e) => <th key={e.id} className="num">{formatarDiaMes(e.em)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {r.linhas.map((l) => (
                    <tr key={l.chave}>
                      <td>{l.rotulo}</td>
                      {l.marcas.map((m, i) => (
                        <td key={i} className={`num marca marca--${m}`} title={LEGENDA_DAS_MARCAS[m]}>
                          {m === 'sim' ? '●' : m === 'nao' ? '○' : '·'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {r.leitura.length > 0 && (
            <div className="doc__leitura">
              {r.leitura.map((l, i) => (
                <div className="doc__leitura__item" key={i}>
                  <b>{l.titulo}</b>
                  <p>{l.texto}</p>
                </div>
              ))}
            </div>
          )}

          {/* Os laudos inteiros: quem quiser conferir a fonte precisa poder. */}
          <details className="doc__laudos">
            <summary>Ver os {r.exames.length === 1 ? 'laudo inteiro' : `${r.exames.length} laudos inteiros`}</summary>
            {r.exames.map((e) => (
              <div className="doc__laudo" key={e.id}>
                <b>{e.nome} — {dia(e.em)}</b>
                <pre>{e.laudo}</pre>
              </div>
            ))}
          </details>
        </section>
      ))}

      {/* ---------------------------------------------------------- legenda */}
      <p className="doc__legenda">
        <b>●</b> {LEGENDA_DAS_MARCAS.sim} · <b>○</b> {LEGENDA_DAS_MARCAS.nao} · <b>·</b> {LEGENDA_DAS_MARCAS.nada}.
        Silêncio não é ausência: um laudo que não fala de um achado não está dizendo que ele não existe.
      </p>

      {/* -------------------------------------------------------- glossário */}
      {dados.glossario.length > 0 && (
        <section className="doc__bloco">
          <h2>O que cada achado é</h2>
          <div className="doc__explicacoes">
            {dados.glossario.map((v) => (
              <div className="doc__explica" key={v.chave}>
                <b>{v.rotulo}</b>
                <p>{v.oQueE}{v.porQueImporta && <> {v.porQueImporta}</>}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------- fontes */}
      {dados.fontes.length > 0 && (
        <section className="doc__bloco">
          <h2>Fontes</h2>
          <ol className="doc__fontes">
            {dados.fontes.map((f) => (
              <li key={f.url}><a href={f.url} target="_blank" rel="noreferrer">{f.titulo}</a><small>{f.url}</small></li>
            ))}
          </ol>
        </section>
      )}

      <footer className="doc__rodape">
        <p className="fraco">saudev1 · registro de apoio da família · gerado em {formatarDataHora(g.em)}</p>
      </footer>
    </article>
  );
}
